import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import CreateStaffForm from "./CreateStaffForm";
import { useAuth } from "../../../context/AuthContext";

const STAFF_SHARE_STORAGE_KEY = "novaxfit_staff_share_cache";

function readShareCache() {
  try {
    const raw = localStorage.getItem(STAFF_SHARE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeShareCache(cache) {
  localStorage.setItem(STAFF_SHARE_STORAGE_KEY, JSON.stringify(cache));
}

export default function StaffPanel() {
  const { authFetch, user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [shareCache, setShareCache] = useState(() => readShareCache());

  const isAdmin = user?.role === "admin";

  const persistShareCache = (nextCache) => {
    setShareCache(nextCache);
    writeShareCache(nextCache);
  };

  const setShareCacheForUser = (userId, payload) => {
    const nextCache = {
      ...shareCache,
      [userId]: payload,
    };
    persistShareCache(nextCache);
  };

  const removeShareCacheForUser = (userId) => {
    const nextCache = { ...shareCache };
    delete nextCache[userId];
    persistShareCache(nextCache);
  };

  const buildShareMessage = ({ email, login_url, temporary_password }) => {
    return [
      "Acceso NovaXFit",
      `Panel: ${login_url}`,
      `Correo: ${email}`,
      `Clave temporal: ${temporary_password}`,
    ].join("\n");
  };

  const loadStaff = async () => {
    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch("/api/gym/admin/users/", {
        method: "GET",
      });

      const data = await response.json().catch(() => []);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo cargar el staff",
        });
        setStaffUsers([]);
        return;
      }

      const filtered = Array.isArray(data)
        ? data.filter((item) => item.role === "staff" || item.role === "admin")
        : [];

      setStaffUsers(filtered);

      const nextCache = { ...shareCache };
      let changed = false;

      filtered.forEach((item) => {
        if (item.role === "staff" && item.must_change_password === false && nextCache[item.id]) {
          delete nextCache[item.id];
          changed = true;
        }
      });

      if (changed) {
        persistShareCache(nextCache);
      }
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al cargar el staff",
      });
      setStaffUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showCreateForm) {
      loadStaff();
    }
  }, [showCreateForm]);

  const filteredStaff = useMemo(() => {
    const term = query.trim().toLowerCase();

    if (!term) return staffUsers;

    return staffUsers.filter((item) => {
      const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim().toLowerCase();
      const email = String(item.email || "").toLowerCase();
      const phone = String(item.phone || "").toLowerCase();

      return fullName.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [staffUsers, query]);

  const openDeleteModal = (item) => {
    setDeleteTarget(item);
  };

  const closeDeleteModal = () => {
    if (actionLoading.startsWith("delete-")) return;
    setDeleteTarget(null);
  };

  const closeShareModal = () => {
    if (actionLoading.startsWith("reset-")) return;
    setShareData(null);
  };

  const copyShareMessage = async () => {
    if (!shareData?.share_message) return;

    try {
      await navigator.clipboard.writeText(shareData.share_message);
      setBanner({
        type: "success",
        message: "Información copiada correctamente",
      });
    } catch {
      setBanner({
        type: "error",
        message: "No se pudo copiar la información",
      });
    }
  };

  const openShareModal = (item) => {
    const cached = shareCache[item.id];

    if (!cached) {
      setBanner({
        type: "error",
        message: "Primero debes generar una clave temporal para poder compartirla.",
      });
      return;
    }

    setShareData(cached);
  };

  const handleDelete = async () => {
    if (!isAdmin || !deleteTarget) return;

    const item = deleteTarget;

    setActionLoading(`delete-${item.id}`);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(`/api/gym/admin/users/${item.id}/delete/`, {
        method: "DELETE",
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo eliminar el usuario",
        });
        return;
      }

      setStaffUsers((prev) => prev.filter((userItem) => userItem.id !== item.id));
      removeShareCacheForUser(item.id);
      setBanner({
        type: "success",
        message: data?.message || "Usuario eliminado correctamente",
      });
      setDeleteTarget(null);
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al eliminar el usuario",
      });
    } finally {
      setActionLoading("");
    }
  };

  const handleResetPassword = async (item) => {
    if (!isAdmin || item.role !== "staff") return;

    setActionLoading(`reset-${item.id}`);
    setBanner({ type: "", message: "" });

    try {
      const response = await authFetch(
        `/api/gym/admin/users/${item.id}/regenerate-temp-password/`,
        {
          method: "POST",
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data?.error || "No se pudo generar la clave temporal",
        });
        return;
      }

      const email = data?.email || data?.user?.email || item.email || "";
      const loginUrl = data?.login_url || "https://nova-xfit.com/admin-login";
      const temporaryPassword = data?.temporary_password || "";
      const preparedShareData = {
        id: item.id,
        first_name: item.first_name || "",
        last_name: item.last_name || "",
        email,
        login_url: loginUrl,
        temporary_password: temporaryPassword,
        share_message:
          data?.share_message ||
          buildShareMessage({
            email,
            login_url: loginUrl,
            temporary_password: temporaryPassword,
          }),
      };

      setStaffUsers((prev) =>
        prev.map((userItem) =>
          userItem.id === item.id
            ? { ...userItem, must_change_password: true }
            : userItem
        )
      );

      setShareCacheForUser(item.id, preparedShareData);
      setShareData(preparedShareData);

      setBanner({
        type: "success",
        message: data?.message || "Clave temporal generada correctamente",
      });
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al generar la clave temporal",
      });
    } finally {
      setActionLoading("");
    }
  };

  const renderStatusChip = (item) => {
    if (item.role === "admin") {
      return (
        <Chip
          label="Admin"
          size="small"
          sx={{
            height: 26,
            borderRadius: "8px",
            fontSize: 12,
            fontWeight: 600,
            bgcolor: "rgba(30,41,59,0.10)",
            color: "#0f172a",
          }}
        />
      );
    }

    if (item.must_change_password) {
      return (
        <Chip
          label="Cambio pendiente"
          size="small"
          sx={{
            height: 26,
            borderRadius: "8px",
            fontSize: 12,
            fontWeight: 600,
            bgcolor: "rgba(220,38,38,0.10)",
            color: "#dc2626",
          }}
        />
      );
    }

    return (
      <Chip
        label="Clave actualizada"
        size="small"
        sx={{
          height: 26,
          borderRadius: "8px",
          fontSize: 12,
          fontWeight: 600,
          bgcolor: "rgba(14,176,122,0.10)",
          color: "#0EB07A",
        }}
      />
    );
  };

  if (showCreateForm && isAdmin) {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.8 }}
        >
          <Box>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: { xs: 20, md: 22 },
                fontWeight: 700,
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
              }}
            >
              Añadir staff
            </Typography>

            <Typography
              sx={{
                color: "rgba(15,23,42,0.56)",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.45,
                mt: 0.5,
              }}
            >
              Completa los datos para generar el acceso.
            </Typography>
          </Box>

          <Button
            onClick={() => setShowCreateForm(false)}
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Regresar
          </Button>
        </Stack>

        <CreateStaffForm />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.4}
        sx={{ mb: 1.8 }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar staff"
            size="small"
            InputProps={{
              startAdornment: (
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#64748b", mr: 0.8 }} />
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                height: 38,
                borderRadius: "10px",
                bgcolor: "#ffffff",
              },
            }}
          />

          <Button
            onClick={loadStaff}
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Recargar
          </Button>
        </Stack>

        {isAdmin && (
          <Button
            onClick={() => setShowCreateForm(true)}
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{
              height: 38,
              px: 1.4,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#ffffff",
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            Añadir staff
          </Button>
        )}
      </Stack>

      {banner.message && (
        <Alert
          severity={banner.type}
          sx={{
            borderRadius: "12px",
            mb: 1.6,
          }}
        >
          {banner.message}
        </Alert>
      )}

      <Paper
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1.2fr 0.8fr",
              md: "1.15fr 1fr 1fr 1.4fr",
            },
            gap: 1,
            px: { xs: 1.6, md: 2 },
            py: 1.3,
            bgcolor: "#f8fafc",
            borderBottom: "1px solid rgba(148,163,184,0.16)",
          }}
        >
          <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
            Usuario
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Correo
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Estado
          </Typography>
          <Typography
            sx={{
              display: { xs: "none", md: "block" },
              fontSize: 12.6,
              fontWeight: 600,
              color: "#475569",
            }}
          >
            Acciones
          </Typography>
        </Box>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#64748b" }}>
              Cargando staff
            </Typography>
          </Stack>
        ) : filteredStaff.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Typography sx={{ fontSize: 13.4, fontWeight: 500, color: "#64748b" }}>
              No se encontraron usuarios.
            </Typography>
          </Box>
        ) : (
          filteredStaff.map((item) => {
            const hasCachedShare = Boolean(shareCache[item.id]);

            return (
              <Box
                key={item.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1.15fr 1fr 1fr 1.4fr",
                  },
                  gap: 1,
                  alignItems: "center",
                  px: { xs: 1.6, md: 2 },
                  py: 1.25,
                  borderBottom: "1px solid rgba(148,163,184,0.12)",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "#0f172a",
                      fontSize: 13.4,
                      fontWeight: 600,
                      lineHeight: 1.35,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {`${item.first_name || ""} ${item.last_name || ""}`.trim() || "Sin nombre"}
                  </Typography>

                  <Typography
                    sx={{
                      display: { xs: "block", md: "none" },
                      color: "#64748b",
                      fontSize: 12.2,
                      fontWeight: 500,
                      lineHeight: 1.35,
                      mt: 0.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.email || "Sin correo"}
                  </Typography>

                  <Box sx={{ display: { xs: "flex", md: "none" }, mt: 1 }}>
                    {renderStatusChip(item)}
                  </Box>
                </Box>

                <Typography
                  sx={{
                    display: { xs: "none", md: "block" },
                    color: "#334155",
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.email || "Sin correo"}
                </Typography>

                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  {renderStatusChip(item)}
                </Box>

                <Stack
                  direction="row"
                  spacing={0.8}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    flexWrap: "wrap",
                  }}
                >
                  {isAdmin && item.role === "staff" && (
                    <Button
                      onClick={() => handleResetPassword(item)}
                      variant="outlined"
                      disabled={actionLoading === `reset-${item.id}`}
                      startIcon={<LockResetRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.6,
                        color: "#334155",
                        borderColor: "rgba(51,65,85,0.18)",
                        "&:hover": {
                          borderColor: "rgba(51,65,85,0.28)",
                          bgcolor: "rgba(148,163,184,0.08)",
                        },
                      }}
                    >
                      {actionLoading === `reset-${item.id}` ? "Procesando" : "Nueva clave"}
                    </Button>
                  )}

                  {isAdmin && item.role === "staff" && (
                    <Button
                      onClick={() => openShareModal(item)}
                      variant="outlined"
                      disabled={!hasCachedShare}
                      startIcon={<ShareRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.6,
                        color: hasCachedShare ? "#334155" : "rgba(51,65,85,0.42)",
                        borderColor: hasCachedShare
                          ? "rgba(51,65,85,0.18)"
                          : "rgba(51,65,85,0.12)",
                        "&:hover": {
                          borderColor: hasCachedShare
                            ? "rgba(51,65,85,0.28)"
                            : "rgba(51,65,85,0.12)",
                          bgcolor: hasCachedShare
                            ? "rgba(148,163,184,0.08)"
                            : "transparent",
                        },
                      }}
                    >
                      Compartir
                    </Button>
                  )}

                  {isAdmin && item.role !== "admin" && (
                    <Button
                      onClick={() => openDeleteModal(item)}
                      variant="outlined"
                      disabled={actionLoading === `delete-${item.id}`}
                      startIcon={<DeleteOutlineRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.6,
                        color: "#dc2626",
                        borderColor: "rgba(220,38,38,0.22)",
                        "&:hover": {
                          borderColor: "rgba(220,38,38,0.32)",
                          bgcolor: "rgba(220,38,38,0.06)",
                        },
                      }}
                    >
                      {actionLoading === `delete-${item.id}` ? "Procesando" : "Eliminar"}
                    </Button>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.8}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    mt: 1,
                    flexWrap: "wrap",
                  }}
                >
                  {isAdmin && item.role === "staff" && (
                    <Button
                      onClick={() => handleResetPassword(item)}
                      variant="outlined"
                      disabled={actionLoading === `reset-${item.id}`}
                      startIcon={<LockResetRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.2,
                        color: "#334155",
                        borderColor: "rgba(51,65,85,0.18)",
                        "&:hover": {
                          borderColor: "rgba(51,65,85,0.28)",
                          bgcolor: "rgba(148,163,184,0.08)",
                        },
                      }}
                    >
                      {actionLoading === `reset-${item.id}` ? "Procesando" : "Nueva clave"}
                    </Button>
                  )}

                  {isAdmin && item.role === "staff" && (
                    <Button
                      onClick={() => openShareModal(item)}
                      variant="outlined"
                      disabled={!hasCachedShare}
                      startIcon={<ShareRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.2,
                        color: hasCachedShare ? "#334155" : "rgba(51,65,85,0.42)",
                        borderColor: hasCachedShare
                          ? "rgba(51,65,85,0.18)"
                          : "rgba(51,65,85,0.12)",
                        "&:hover": {
                          borderColor: hasCachedShare
                            ? "rgba(51,65,85,0.28)"
                            : "rgba(51,65,85,0.12)",
                          bgcolor: hasCachedShare
                            ? "rgba(148,163,184,0.08)"
                            : "transparent",
                        },
                      }}
                    >
                      Compartir
                    </Button>
                  )}

                  {isAdmin && item.role !== "admin" && (
                    <Button
                      onClick={() => openDeleteModal(item)}
                      variant="outlined"
                      disabled={actionLoading === `delete-${item.id}`}
                      startIcon={<DeleteOutlineRoundedIcon />}
                      sx={{
                        height: 34,
                        px: 1.2,
                        borderRadius: "9px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 12.2,
                        color: "#dc2626",
                        borderColor: "rgba(220,38,38,0.22)",
                        "&:hover": {
                          borderColor: "rgba(220,38,38,0.32)",
                          bgcolor: "rgba(220,38,38,0.06)",
                        },
                      }}
                    >
                      {actionLoading === `delete-${item.id}` ? "Procesando" : "Eliminar"}
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })
        )}
      </Paper>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          Eliminar staff
        </DialogTitle>

        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography
            sx={{
              color: "#475569",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            {deleteTarget
              ? `Se eliminará a ${deleteTarget.first_name || ""} ${deleteTarget.last_name || ""}.`
              : ""}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.4, pt: 0.8 }}>
          <Button
            onClick={closeDeleteModal}
            variant="outlined"
            disabled={actionLoading.startsWith("delete-")}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={actionLoading.startsWith("delete-")}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            {actionLoading.startsWith("delete-") ? "Eliminando" : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(shareData)}
        onClose={closeShareModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          Compartir acceso
        </DialogTitle>

        <DialogContent sx={{ pt: "10px !important" }}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                borderRadius: "14px",
                border: "1px solid rgba(148,163,184,0.18)",
                bgcolor: "#f8fafc",
                p: 1.6,
              }}
            >
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.8,
                }}
              >
                Correo
              </Typography>
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: 13.4,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  mb: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {shareData?.email || "-"}
              </Typography>

              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.8,
                }}
              >
                URL de acceso
              </Typography>
              <Typography
                sx={{
                  color: "#475569",
                  fontSize: 13.4,
                  fontWeight: 500,
                  lineHeight: 1.5,
                  mb: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {shareData?.login_url || "-"}
              </Typography>

              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.8,
                }}
              >
                Clave temporal
              </Typography>
              <Typography
                sx={{
                  color: "#dc2626",
                  fontSize: 20,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: "0.08em",
                  mb: 1.6,
                }}
              >
                {shareData?.temporary_password || "-"}
              </Typography>

              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 0.8,
                }}
              >
                Mensaje para copiar
              </Typography>

              <Box
                sx={{
                  borderRadius: "12px",
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(148,163,184,0.18)",
                  p: 1.4,
                }}
              >
                <Typography
                  sx={{
                    color: "#334155",
                    fontSize: 13.2,
                    fontWeight: 500,
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {shareData?.share_message || ""}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.4, pt: 1 }}>
          <Button
            onClick={closeShareModal}
            variant="outlined"
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              color: "#334155",
              borderColor: "rgba(51,65,85,0.18)",
              "&:hover": {
                borderColor: "rgba(51,65,85,0.28)",
                bgcolor: "rgba(148,163,184,0.08)",
              },
            }}
          >
            Cerrar
          </Button>

          <Button
            onClick={copyShareMessage}
            variant="contained"
            startIcon={<ContentCopyRoundedIcon />}
            sx={{
              height: 36,
              px: 1.4,
              borderRadius: "9px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.8,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#111827",
                boxShadow: "none",
              },
            }}
          >
            Copiar información
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}